/**
 * Typed custom events for cross-component communication.
 *
 * Centralizes all window-level custom events so implicit dependencies
 * become visible and type-checked at compile time.
 *
 * @example
 * // Dispatch
 * dispatchAppEvent('route-change', '/home')
 *
 * // Listen (returns cleanup function for useEffect)
 * useEffect(() => onAppEvent('tab-drag-move', () => remeasure()), [])
 */

interface AppEventMap {
  /** Fired by RouteTracker when React Router location changes. Payload: pathname */
  'route-change': string
  /** Fired by TabBar during active tab drag. No payload. */
  'tab-drag-move': undefined
  /** Fired by TabBar when drag ends. No payload. */
  'tab-drag-end': undefined
  /** Suppress scroll-spy during programmatic scrolls (CMD/Alt+Arrow, sidebar click). */
  'scroll-spy-suppress': undefined
  /** Set the active section in the sidebar after keyboard navigation. Payload: sectionId or null. */
  'scroll-spy-set-section': string | null
  /** Scroll to a section header (sidebar sub-menu click or initial load). Payload: sectionId. */
  'scroll-to-section': string
  /** Fired after the current user's profile (avatar, name, …) was updated. Listeners refetch. */
  'user-profile-updated': undefined
}

type EventPayload<K extends keyof AppEventMap> = AppEventMap[K]

/** Dispatch a typed custom event on `window`. */
export function dispatchAppEvent<K extends keyof AppEventMap>(
  type: K,
  ...args: EventPayload<K> extends undefined ? [] : [EventPayload<K>]
): void {
  window.dispatchEvent(new CustomEvent(type, { detail: args[0] }))
}

/** Subscribe to a typed custom event. Returns an unsubscribe function. */
export function onAppEvent<K extends keyof AppEventMap>(
  type: K,
  handler: EventPayload<K> extends undefined ? () => void : (payload: EventPayload<K>) => void,
): () => void {
  const listener = (e: Event) => {
    (handler as (payload: any) => void)((e as CustomEvent).detail)
  }
  window.addEventListener(type, listener)
  return () => window.removeEventListener(type, listener)
}
